<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { menuRoutesEnum } from '@/router'

import * as dataGrid from '@/composables/data-grid'
import { useAttachment } from '@/composables/registry/service-attachment-grid'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxButton } from 'devextreme-vue/button'
import { DxPopup } from 'devextreme-vue/popup'

import AttachmentPreview from '@/components/shared/AttachmentPreview.vue'
import AttachmentUploaderPopup from '@/components/shared/AttachmentUploaderPopup.vue'
import DocumentReferenceTemplate from '@/components/shared/DocumentReferenceTemplate.vue'

import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

const props = defineProps({
  data: {
    type: Object,
    required: true
  },
  documentName: {
    type: String,
    default: ''
  },
  hasAttachments: {
    type: Boolean,
    required: true
  },
  documentField: {
    type: String,
    required: true
  },
  formDataSource: {
    type: Object,
    default: () => {}
  },
  refreshGridAsync: {
    type: Function,
    default: () => {}
  },
  entitiesId: {
    type: Object,
    default: () => {}
  },
})

const i18n = getI18n()
const documentLabel = computed(() => props.data?.series && props.data?.number_mnemonic ? `${props.data?.number_mnemonic}/${props.data?.series}` : `${props.data?.name}`)
const title = computed(() => {
  const customer = registryStore.customers.find(c => c.id === props.data.customer_id) || null
  return `${i18n.t('common.attachment', 2)}: ${i18n.t(props.documentName)} - ${documentLabel.value} ${customer ? customer?.name : props.data?.supplier_name ? props.data?.supplier_name : ''}`
})

const registryStore = useRegistryStore()
const portalStore = usePortalStore()

const popupVisible = ref(false)
const showUploadDialog = ref(false)
const gridOptions = ref(null)
const filterToApply = ref(false)
const gridId = 'icon-attachments-grid'
const { openFile, getAttachmentFilter } = useAttachment()

const popupToolbar = computed(() => [
  {
    widget: 'dxButton',
    toolbar: 'bottom',
    location: 'after',
    options: {
      text: i18n.t('common.viewAll'),
      onClick: () => {
        const target = menuRoutesEnum.SERVICE_ATTACHMENTS
        const url = `${window.location.origin}${target}`
        window.open(url, '_blank')
      },
    },
  },
  {
    widget: 'dxButton',
    toolbar: 'bottom',
    location: 'after',
    options: {
      text: i18n.t('common.upload'),
      onClick: () => {
        popupVisible.value = false
        showUploadDialog.value = true
      },
    },
  },
  {
    widget: 'dxButton',
    toolbar: 'bottom',
    location: 'after',
    options: {
      text: i18n.t('common.cancel'),
      onClick: () => { props.refreshGridAsync(); popupVisible.value = false },
    },
  },
])

const directReferenceField = props.documentField === 'attachment_subject_ref' ? props.documentField : `${props.documentField}_id`

const columns = [
  {
    dataField: 'attachment_file_name',
    caption: i18n.t('common.fileName'),
    cellTemplate: 'attachmentFileNameTemplate',
    width: '20rem',
    allowGrouping: false,
  },
  {
    name: 'attachmentReferenceGroup',
    dataField: 'attachment_reference',
    caption: i18n.t('common.document', 2),
    groupIndex: 0,
    visible: false,
    showInColumnChooser: false,
    calculateGroupValue: (rowData) => rowData[directReferenceField] === props.data?.id ? '' : rowData.attachment_reference ?? '',
    groupCellTemplate: (cellElement, cellInfo) => { cellElement.textContent = cellInfo.value },
  },
  {
    dataField: 'attachment_reference',
    cellTemplate: 'documentsRef',
    caption: i18n.t('common.document', 2),
    width: '16rem',
    allowGrouping: false,
  },
  {
    dataField: 'attachment_category',
    caption: i18n.t('common.category'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedAttachmentCategories(), displayExpr: 'name' }),
    width: '8rem',
    allowGrouping: false,
  },
  {
    dataField: 'attachment_subcategory',
    caption: i18n.t('common.subcategory'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedAttachmentSubcategories(), displayExpr: 'name' }),
    width: '8rem',
    allowGrouping: false,
  },
  {
    dataField: 'attachment_tag',
    caption: i18n.t('common.tag'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedAttachmentTags(), displayExpr: 'name' }),
    cellTemplate: 'attachmentTagCellTemplate',
    allowGrouping: false,
  },
  {
    dataField: 'attachment_description',
    caption: i18n.t('common.description'),
    width: '20rem',
    allowGrouping: false,
  },
]

const editing = dataGrid.useDefaultEditing(null, {}, {
  allowUpdating: false,
  allowAdding: false,
  allowDeleting: true,
})

const onPopupShown = () => {
  filterToApply.value = true
  if (!gridOptions.value) {
    const url = `${registryStore.api.getAbsoluteUrl()}${registryStore.api.serviceAttachmentsUrl}`

    gridOptions.value = dataGrid.useDefaultGridOptions({
      id: gridId,
      dataSource: dataGrid.useODataSource(url, {
        customParamsFn: () => {
          const params = { $top: 500 }
          if (props.data?.id != null) {
            params.$orderByField = props.documentField
            params.$orderByValue = props.data.id
          }
          return params
        },
        filter: getAttachmentFilter(props.documentField, props.data?.id, props.entitiesId),
      }),
      height: '100%',
      columns: columns,
      editing,
      allowColumnResizing: true,
      columnChooser: { enabled: false },
      remoteOperations: {
        filtering: true,
        sorting: true,
      },
      groupPanel: { visible: false},
      grouping: {
        contextMenuEnabled: true,
        autoExpandAll: false,
      },
      onRowPrepared: (e) => {
        if (e.rowType === 'group' && e.values?.[0] === '') { e.rowElement.hidden = true }
      },
      onContentReady: (e) => {
        if (filterToApply.value) {
          filterToApply.value = false
          e.component.columnOption('attachment_category', 'filterValue', props.formDataSource?.attachment_category ?? null)
        }
        const directGroupRow = e.component.getVisibleRows().find((row) => row.rowType === 'group' && row.key?.[0] === '')
        if (directGroupRow && !e.component.isRowExpanded(directGroupRow.key)) { e.component.expandRow(directGroupRow.key) }
      },
      onInitialized: (e) => dataGrid.useDefaultOnInitialized(e, { enableGrouping: true }),
    })
  } else {
    gridOptions.value.dataSource.reload()
  }
}

const openPopup = () => {
  popupVisible.value = true
}
</script>

<template>
  <div style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%;">
    <dx-button
      icon="mdi mdi-paperclip"
      styling-mode="text"
      type="normal"
      :class="hasAttachments ? 'clip-active' : 'clip-inactive'"
      @click="openPopup"
    />
    <attachment-uploader-popup
      v-if="showUploadDialog"
      :show-dialog="showUploadDialog"
      :form-data-source="props.formDataSource"
      :is-readonly="true"
      :entities-id="props.entitiesId"
      @on-save="props.refreshGridAsync(); showUploadDialog = false"
      @on-cancel="showUploadDialog = false"
    />
    <dx-popup
      v-model:visible="popupVisible"
      :drag-enabled="true"
      :resize-enabled="true"
      :width="'80rem'"
      :height="'40rem'"
      :title="title"
      :show-title="true"
      :toolbar-items="popupToolbar"
      @shown="onPopupShown"
    >
      <template #content>
        <dx-data-grid
          v-if="gridOptions"
          v-show="!filterToApply"
          ref="attachmentGridRef"
          v-bind="gridOptions"
        >
          <template #documentsRef="{ data: cellInfo }">
            <document-reference-template :cell-info="cellInfo" />
          </template>
          <template #attachmentFileNameTemplate="{ data: cellInfo }">
            <div>
              <a
                href="#"
                style="text-decoration: underline"
                @click.prevent="openFile(cellInfo.data)"
              >
                {{ cellInfo.data.attachment_file_name }}
              </a>
              <q-tooltip
                self="top middle"
                style="background-color: transparent"
              >
                <q-card>
                  <attachment-preview
                    :id="cellInfo.data.id"
                    :data="cellInfo.data"
                  />
                </q-card>
              </q-tooltip>
            </div>
          </template>
          <template #attachmentTagCellTemplate="{ data: templateOptions }">
            <q-chip
              size="sm"
              text-color="white"
              style="margin: 0"
              :label="portalStore.getLocalizedAttachmentTags().find(x => x.id === templateOptions.data.attachment_tag)?.name"
              :color="portalStore.getLocalizedAttachmentTags().find(x => x.id === templateOptions.data.attachment_tag)?.color"
            />
          </template>
        </dx-data-grid>
      </template>
    </dx-popup>
  </div>
</template>

<style scoped>
:deep(.clip-inactive .dx-icon) {
  color: #9e9e9e !important;
}
:deep(.clip-active .dx-icon) {
  color: #1976d2 !important;
}
</style>
